import { ParamType } from "../../types/grid.ts";
import type { InputParam } from "../../types/grid.ts";
import type { GenericPrice } from "../../api/price.ts";
import type { DbFeatureFlags } from "../../util/dbFeatureFlags.ts";
import { WEIGHT, calcWeight, makeGenericPrice, rowKey, type ScopePart } from "../../util/pricingCascade.ts";

// Price-domain primitives (weight bitmask, weight calc, scope key, GenericPrice
// builder) are owned by pricingCascade. Re-export under the names this module's
// consumers already use so callers don't change.
export { WEIGHT, calcWeight };
/** Alias of pricingCascade.rowKey — identical key format. */
export const scopeKey = rowKey;

/* ── View price column name → PriceType id (must match Web_Generic_Price_View) ── */
export const PRICE_COLUMN_TO_TYPE: Record<string, number> = {
  Price: 1, Tax: 2, Rental: 3, RentalTax: 4, EHPrice: 5, EHTax: 6,
  ReserveLatePeriodPrice: 7, ReserveLateTax: 8, ReservePeriodPrice: 9,
  AdvTax1: 11, AdvTax2: 12, AdvTax3: 13, AdvTax4: 14, AdvTax5: 15,
};
export const PRICE_TYPE_TO_COLUMN: Record<number, string> = Object.fromEntries(
  Object.entries(PRICE_COLUMN_TO_TYPE).map(([k, v]) => [v, k]),
);
/** Editable price columns, in display order. */
export const PRICE_COLUMNS = [
  "Price", "Tax", "Rental", "RentalTax", "EHPrice", "EHTax",
  "ReserveLatePeriodPrice", "ReserveLateTax", "ReservePeriodPrice",
  "AdvTax1", "AdvTax2", "AdvTax3", "AdvTax4", "AdvTax5",
] as const;

/** The single price category shown at a time across the price surfaces (advanced
 *  grid + kiosk/location tabs + product editor). Reserve REUSES the rental Rental
 *  Price + Rental Tax Rate, so it surfaces those alongside its own Period/Late. */
export type PriceCategory = "normal" | "eathere" | "rental" | "reserve";
export const CATEGORY_COLUMNS: Record<PriceCategory, string[]> = {
  normal: ["Price", "Tax", "AdvTax1", "AdvTax2", "AdvTax3", "AdvTax4", "AdvTax5"],
  eathere: ["Price", "Tax", "EHPrice", "EHTax"],
  rental: ["Price", "Tax", "Rental", "RentalTax"],
  reserve: ["Price", "Tax", "Rental", "RentalTax", "ReservePeriodPrice", "ReserveLatePeriodPrice", "ReserveLateTax"],
};

/** A price column paired with its tax column — the units the net/gross calculator
 *  operates on. Standalone columns (ReservePeriodPrice, AdvTax*) have no pair. */
export interface PriceTaxPair {
  labelPrice: string;
  labelTax: string;
  priceCol: string;
  taxCol: string;
  priceType: number;
  taxType: number;
  /** True when the "tax" column is a % RATE (PT4 Rental Tax Rate / PT8 Late Taxrate),
   *  NOT a flat amount. The calc cell then stores the rate verbatim and derives the
   *  net price against it (price+rate), instead of deriving a tax amount. */
  taxIsRate?: boolean;
}
export const PRICE_TAX_PAIRS: PriceTaxPair[] = [
  { labelPrice: "Price", labelTax: "Tax", priceCol: "Price", taxCol: "Tax", priceType: 1, taxType: 2 },
  { labelPrice: "EH Price", labelTax: "EH Tax", priceCol: "EHPrice", taxCol: "EHTax", priceType: 5, taxType: 6 },
  { labelPrice: "Rental", labelTax: "Rental Tax", priceCol: "Rental", taxCol: "RentalTax", priceType: 3, taxType: 4, taxIsRate: true },
  { labelPrice: "Reserve Late", labelTax: "Reserve Late Tax", priceCol: "ReserveLatePeriodPrice", taxCol: "ReserveLateTax", priceType: 7, taxType: 8, taxIsRate: true },
];

/** Calc-eligible price/tax pairs for the active DB flags. When the Late Taxrate (PT8)
 *  is hidden (`ProductHideReserveLateTaxrate`), drop the Reserve-Late pair so Net/Gross
 *  mode never folds PT7 against — and thereby exposes an editable — that hidden rate;
 *  PT7 then stays a plain direct price column. */
export function calcTaxPairs(hideLateTaxRate: boolean): PriceTaxPair[] {
  return hideLateTaxRate ? PRICE_TAX_PAIRS.filter((p) => p.taxType !== 8) : PRICE_TAX_PAIRS;
}

/**
 * Price columns to hide on the grid for the active DB feature flags.
 * Mirrors the product editor's blocks (editProductAccess.tsx):
 *   Rental block (3/4), Eat-Here block (5/6), Reserve block (7/8/9).
 * Absent flag = falsy = hide the block (same default as getDbFeatureFlags).
 */
export function hiddenPriceColumns(flags: DbFeatureFlags): string[] {
  const hidden: string[] = [];
  if (!flags.ProductShowRental) hidden.push("Rental", "RentalTax");
  if (!flags.ProductShowEatHere) hidden.push("EHPrice", "EHTax");
  if (!flags.ProductShowReserve) hidden.push("ReserveLatePeriodPrice", "ReserveLateTax", "ReservePeriodPrice");
  else if (flags.ProductHideReserveLateTaxrate) hidden.push("ReserveLateTax");
  if (!flags.ProductShowAdvTax) hidden.push("AdvTax1", "AdvTax2", "AdvTax3", "AdvTax4", "AdvTax5");
  return hidden;
}

/* ── Scope of one override row ────────────────────────────────────────── */
export interface PriceScope {
  Product: number; Type: number; Category: number;
  Kiosk: number; Location: number; Chain: number;
  District: number; State: number; OperatorID: number;
}

/* ── Pending edits: one entry per edited scope row ────────────────────── */
export interface PendingRow {
  scope: PriceScope;
  /** PriceType id → new value, or null to delete that price. */
  prices: Record<number, number | null>;
  /** Actual stored scope of the source grid row (name-resolved, present-including-0
   *  via scopePartsFromRaw), captured at stage time. Lets "Changes Made" show the
   *  real scope of an existing override — e.g. a legacy 0-filled row reads
   *  "Operator: 0 + … + Kiosk: 997" rather than the 0→null-coerced "Kiosk: 997".
   *  Absent for add/from-file rows (no source row) → fall back to the intended scope. */
  rawScopeParts?: ScopePart[];
}
export type PendingEdits = Map<string, PendingRow>;

/** Build the GenericPrice[] payload for SaveAdvancedPriceData from pending edits. */
export function buildSaveItems(pending: PendingEdits): GenericPrice[] {
  const items: GenericPrice[] = [];
  for (const { scope, prices } of pending.values()) {
    const weight = calcWeight(scope);
    for (const [ptStr, value] of Object.entries(prices)) {
      const pt = Number(ptStr);
      if (value === undefined) continue;
      items.push(makeGenericPrice(scope, pt, value, weight));
    }
  }
  return items;
}

/* ── Tabs ─────────────────────────────────────────────────────────────── */
export type ScopeTab = "product" | "operator" | "regional" | "location" | "kiosk" | "changes";

export const SCOPE_TABS: { key: ScopeTab; label: string }[] = [
  { key: "product", label: "Product Defaults" },
  { key: "operator", label: "By Operator" },
  { key: "regional", label: "Regional" },
  { key: "location", label: "By Location" },
  { key: "kiosk", label: "By Kiosk" },
  { key: "changes", label: "Changes Made" },
];

/** Static filter on Weight for each server-backed tab (none for "changes"). */
export function tabStaticFilters(tab: ScopeTab): InputParam[] {
  switch (tab) {
    case "product":  return [{ sColumn: "Weight", Type: ParamType.Equal, oValue: WEIGHT.Product }];
    case "operator": return [{ sColumn: "Weight", Type: ParamType.InBit, oValue: String(WEIGHT.OperatorID) }];
    case "location": return [{ sColumn: "Weight", Type: ParamType.InBit, oValue: String(WEIGHT.Location) }];
    case "kiosk":    return [{ sColumn: "Weight", Type: ParamType.InBit, oValue: String(WEIGHT.Kiosk) }];
    case "regional": return [{ sColumn: "Weight", Type: ParamType.InBit, oValue: `${WEIGHT.State},${WEIGHT.District},${WEIGHT.Chain}` }];
    default:         return [];
  }
}

/** Read a PriceScope out of a grid row using the column-name → index map. */
export function scopeFromRow(rowData: unknown[], colIndex: Record<string, number>): PriceScope {
  const num = (name: string): number => {
    for (const k of Object.keys(colIndex)) {
      if (k.toLowerCase() === name.toLowerCase()) {
        const v = rowData[colIndex[k]!];
        const n = typeof v === "number" ? v : parseInt(String(v ?? ""), 10);
        return Number.isFinite(n) ? n : 0;
      }
    }
    return 0;
  };
  return {
    Product: num("Product"), Type: num("Type"), Category: num("Category"),
    Kiosk: num("Kiosk"), Location: num("Location"), Chain: num("Chain"),
    District: num("District"), State: num("State"), OperatorID: num("OperatorID"),
  };
}
